/**
 * Format-tolerant field equality — shared conflict-detection primitives.
 *
 * Extracted from the Closing Doc Assistant's ClosedLoanReviewModal (which keeps
 * its own inline copy for now — a deliberate short-term duplication; the shipped
 * closing flow is left untouched). Used by the Intake Enrich review to decide
 * when an extracted value genuinely differs from the current Zoho value.
 */

export type FieldKind =
  | "currency"
  | "number"
  | "date"
  | "text"
  | "multiline"
  | "picklist"
  | "boolean";

/** Strip $ , % whitespace → finite number or null. */
export function parseNum(s: string): number | null {
  const cleaned = s.replace(/[$,%\s]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** Numbers/rates compared rounded to 2dp (12.99 == 12.990, 1000 == 1000.00). */
export function numEq(a: string, b: string): boolean {
  const na = parseNum(a);
  const nb = parseNum(b);
  if (na == null || nb == null) return na === nb;
  return Math.round(na * 100) === Math.round(nb * 100);
}

/** Normalize a date ("2026-05-28" or "May 28, 2026" or "5/28/2026") to ISO. */
export function toISO(s: string): string | null {
  const t = s.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const d = new Date(t);
  if (isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

export function dateEq(a: string, b: string): boolean {
  const ia = toISO(a);
  const ib = toISO(b);
  return ia != null && ib != null && ia === ib;
}

/** Free-text equality: trim + lowercase. */
export function strEq(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Compare two string-valued fields by kind. Picklists compare exact (trim only)
 * since their values are canonical Zoho strings; free text is case-insensitive.
 * Booleans are compared as booleans by the caller, not here.
 */
export function eqByKind(kind: FieldKind, a: string, b: string): boolean {
  switch (kind) {
    case "currency":
    case "number":
      return numEq(a, b);
    case "date":
      return dateEq(a, b);
    case "picklist":
      return a.trim() === b.trim();
    default:
      return strEq(a, b); // text | multiline
  }
}
